/**
 * Hook: GET /api/t/{slug}/notes/{candidate_id} — cursor-paginated list
 * (Batch 30j-6).
 *
 * Returns `useInfiniteQuery` over `CursorPage[NoteOut]`. Newest-first.
 * Tab-activation gated via the `enabled` opt — when the user is on a
 * different candidate-page tab, the queryFn is NEVER invoked.
 *
 * Design references:
 *   - `frontend/docs/30j_6_design.md` §0.1 — backend contract verified
 *   - `frontend/docs/30j_6_architect.md` §2.1 — hook signature + cache key
 *   - `frontend/docs/30j_6_security.md` §2.4 — cursor opacity + HARD-5
 *
 * Trust posture (cursor): the `cursor` query param is opaque server-issued
 * state. The frontend MUST NOT decode, inspect, or construct cursor
 * values. Backend at `services/notes.list_notes_for_candidate` decodes
 * the cursor as a `(created_at, id)` keyset; tampered cursors cannot
 * cross tenant boundaries (RLS scopes by `current_user.tenant_id`).
 * See `frontend/docs/30j_6_architect.md` §0.3.
 */
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";

export interface NoteOut {
  id: string;
  tenant_id: string;
  user_id: string;
  candidate_id: string;
  content: string;
  /** ISO 8601 string from backend `serialize_dt`. */
  created_at: string;
  /**
   * ISO 8601 string set on edit; `null` = never edited. Drives the FE
   * "edited" indicator (P2 demo-friction fix, 2026-06-16).
   */
  edited_at?: string | null;
}

export interface NotesPage {
  items: NoteOut[];
  next_cursor: string | null;
  has_more: boolean;
}

interface UseNotesOpts {
  candidateId: string | undefined;
  /** Tab-activation gate. Pass `activeTab === "notes"` from the consumer. */
  enabled: boolean;
}

const PAGE_SIZE = 25;

export function useNotes(
  opts: UseNotesOpts,
): UseInfiniteQueryResult<InfiniteData<NotesPage, string | null>, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { candidateId, enabled } = opts;

  return useInfiniteQuery<
    NotesPage,
    ApiError,
    InfiniteData<NotesPage, string | null>,
    readonly unknown[],
    string | null
  >({
    // Cache key isolates per-(tenant, candidate). NEVER shared across
    // candidates; cross-candidate cache leakage is structurally impossible.
    queryKey: ["notes", "by-candidate", tenantSlug, candidateId] as const,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = { limit: String(PAGE_SIZE) };
      if (pageParam) params["cursor"] = pageParam;
      const r = await apiClient.get<NotesPage>(
        `/api/t/${tenantSlug}/notes/${candidateId}`,
        { params },
      );
      return r.data;
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: enabled && !!tenantSlug && !!candidateId,
    // Cached pages stay "fresh" for 30s so a tab cycle does NOT trigger
    // an immediate refetch (matches useInterviewAnalysis.ts canon).
    staleTime: 30_000,
    retry: (failureCount) => failureCount < 2,
  });
}
